import KroProgressBar, {
  variant,
} from "features/components/progress/kroProgressBar";
import { useLocation, useParams } from "react-router-dom";
import { RelatedArticlesCarousel } from "../components/relatedArticlesCarousel";
import WebsiteFooter from "common/layout/websiteFooter";
import { useEffect, useRef, useState } from "react";
import { Articles } from "../../data/models/article";
import Markdown from "react-markdown";
import { format } from "date-fns";
import useGetArticles from "../hooks/useGetArticles";
import { SocialShare } from "../components/socialShare";
import { SeoProvider } from "features/components/utils/seoContext";
import { SeoMetaDecorator } from "features/components/utils/seoMetaDecorator";
import { useGetArticle } from "../hooks/useGetArticle";
import KroLoadingIndicator, {
  LoaderType,
} from "features/components/loader/kroCircularIndicator";

export function BlogPostPage() {
  const { state } = useLocation();

  const stateArticle = state as Articles;
  const [scrollProgress, setScrollProgress] = useState(0);
  const [readTime, setReadTime] = useState(0);
  const { slug } = useParams<{ slug: string }>();
  const { data: articles } = useGetArticles();
  const { data: articleData, isLoading: isLoadingArticle } = useGetArticle(
    slug ?? "",
  );

  // Use fetched article data if available, otherwise fall back to state data
  const article = articleData?.data[0] || stateArticle;

  // Refs for content
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate read time
  useEffect(() => {
    if (article?.content) {
      // Average reading speed (words per minute)
      const wordsPerMinute = 200;

      // Count words in content
      const wordCount = article.content.split(/\s+/).length;

      // Calculate read time in minutes
      const minutes = Math.ceil(wordCount / wordsPerMinute);
      setReadTime(minutes);
    }
  }, [article]);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const content = contentRef.current;
      if (!content) return;

      const contentHeight = content.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      const progress =
        (scrollPosition / (contentHeight - viewportHeight)) * 100;

      setScrollProgress(Math.min(Math.max(progress, 0), 100));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show loading state if we're loading and don't have state data
  if (isLoadingArticle && !stateArticle) {
    return <KroLoadingIndicator type={LoaderType.FIXED} />;
  }

  // Show error state if we have no article data
  if (!article) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Article not found</h1>
          <p className="mt-2">The article you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "MMMM do, yyyy");
    } catch (error) {
      return dateString;
    }
  };

  return (
    <SeoProvider
      value={{
        title: article?.seo?.metaTitle || "",
        description: article?.seo?.metaDescription || "",
        url: window.location.href,
        shareImage: article?.seo?.sharedImage?.data?.attributes?.url
          ? `${process.env.REACT_APP_STRAPI_URL}${article.seo.sharedImage.data.attributes.url}`
          : "",
        keywords: article?.seo?.keywords?.replaceAll(",", " ") || "",
        preventIndexing: article?.seo?.preventIndexing || false,
      }}
    >
      <SeoMetaDecorator />
      <div className="mx-[16px] mt-[40px] flex flex-col lg:mx-[75px] lg:mt-[80px] lg:flex-row lg:gap-[64px]">
        <p className="flex-[1.5] font-inter text-[30px] font-medium leading-[125%] tracking-[-0.02em] lg:font-dm-sans lg:text-[64px] lg:leading-[100%] lg:tracking-[-0.02em]">
          {article?.title}
        </p>
        <p className="mt-[20px] flex-1 font-inter text-[14px] font-normal leading-[140%] tracking-[0%] text-[#5A5A59] lg:text-[#131313]">
          {article?.description}
        </p>
        <div className="mt-[32px] flex w-[271px] items-center justify-center rounded-[60px] bg-[#F6F6F6] py-[16px] lg:hidden lg:max-w-[303px]">
          <p className="font-inter text-[16px] font-medium leading-[100%] tracking-[0%] text-[#5A5A59]">
            Updated on {formatDate(article?.updatedAt ?? "")}
          </p>
        </div>
        <div className="mt-[24px] flex flex-row gap-[8px] lg:hidden">
          <div className="flex items-center justify-center rounded-[33.9px] border-[0.45px] border-[#D2D2D2] px-[12px] py-[8px]">
            <p className="font-inter text-[12px] font-medium leading-[100%] tracking-[0%] text-[#5A5A59]">
              {formatDate(article?.createdAt ?? "")}
            </p>
          </div>
          <div className="flex items-center justify-center rounded-[33.9px] border-[0.45px] border-[#D2D2D2] px-[12px] py-[8px]">
            <p className="font-inter text-[12px] font-medium leading-[100%] tracking-[0%] text-[#5A5A59]">
              {readTime} min read
            </p>
          </div>
        </div>
      </div>
      <div className="mx-[16px] mt-[24px] lg:mx-[75px] lg:mt-[64px]">
        {/* <img
          src={`${process.env.REACT_APP_STRAPI_URL}${article.attributes.cover.data.attributes.formats.large.url}`}
          alt="blogPostImage"
          className="w-full max-w-full rounded-[20px]"
        /> */}
        <div className="mt-[30px] flex flex-col items-start lg:hidden">
          <SocialShare title={article?.title || ""} />
        </div>
      </div>
      <div className="mx-[16px] flex flex-col lg:mx-[75px] lg:mt-[128px] lg:flex-row lg:gap-[32px]">
        <div className="hidden h-[500px] w-full flex-col lg:flex lg:flex-[0.35]">
          <div className="flex h-[44px] w-full flex-row items-center justify-between rounded-[60px] border-[0.8px] border-[#D2D2D2] px-[15px] py-[15px]">
            <div className="flex items-center">
              <img src="/assets/icons/time.svg" alt="time" />
              <p className="ml-[6px] text-[14px] font-medium leading-[100%] tracking-[0%] text-[#131313]">
                {readTime} min read
              </p>
            </div>
            <KroProgressBar
              amountRaised={scrollProgress.toString()}
              amountToBeRasied={"100"}
              varient={variant.solid}
              customStyle="w-[120px] sm:w-[150px] md:w-[180px] lg:w-[200px]"
              backgroundColor="#022928"
            />
          </div>
          <div className="mt-[24px] flex h-[438px] flex-col items-center justify-center rounded-[42px] bg-[#FFF3ED] px-[24px] sm:px-[32px] md:px-[20px]">
            <p className="font-inter text-[30px] font-medium leading-[125%] tracking-[-0.02em] text-[#131313]">
              Don't miss our Daily update
            </p>
            <p className="mt-[24px] font-inter text-[14px] leading-[140%] tracking-[0%] text-[#5A5A59]">
              Passionate about creating engaging content and connecting with
              audiences through meaningful storytelling.
            </p>
            <input
              placeholder="Enter your email"
              className="mt-[24px] h-[48px] w-full border-b border-[#022928] bg-[#FFF3ED] px-[16px] py-[12px] text-[14px] font-medium leading-[100%] tracking-[0%] text-[#131313] focus:border-b focus:outline-none"
            />
            <button className="mt-[40px] flex h-[72px] max-h-[72px] w-full transform flex-row items-center justify-center gap-[12px] rounded-[90px] bg-[#022928] transition-all duration-300 hover:scale-110">
              <p className="font-inter text-[18px] font-medium leading-[100%] tracking-[-0.01em] text-[#FFFEFD]">
                Subscribe
              </p>
              <img src="/assets/icons/Arrow.svg" alt="arrow-right" />
            </button>
          </div>
        </div>

        <div ref={contentRef} className="w-full flex-1">
          <div className="hidden items-center justify-center rounded-[60px] bg-[#F6F6F6] py-[16px] lg:max-w-[303px]">
            <p className="font-inter text-[16px] font-medium leading-[100%] tracking-[0%] text-[#5A5A59]">
              Updated on {formatDate(article?.updatedAt ?? "")}
            </p>
          </div>
          <div className="mb-[32px] hidden items-center justify-center rounded-[60px] bg-[#F6F6F6] py-[16px] lg:flex lg:max-w-[303px]">
            <p className="font-inter text-[16px] font-medium leading-[100%] tracking-[0%] text-[#5A5A59]">
              Updated on {formatDate(article?.updatedAt ?? "")}
            </p>
          </div>
          <div className="mt-[32px] flex flex-col gap-[16px] lg:mt-0 lg:flex-row lg:items-start">
            <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[60px] border border-[#D2D2D2]">
              <p className="font-inter text-[32px] leading-[160%] tracking-[0%] text-[#131313]">
                {article?.author?.charAt(0)}
              </p>
            </div>
            <div className="flex-1"></div>
          </div>
          <div className="mt-[56px] flex flex-col gap-[16px] lg:items-start">
            <Markdown>{article?.content || ""}</Markdown>
          </div>
          <div className="mt-[72px] w-full border-t border-[#D2D2D2] lg:mx-[20px]"></div>
          <div className="flex flex-col items-start lg:mt-[24px] lg:flex-row lg:justify-between lg:gap-[16px]">
            <div className="mt-[24px] flex flex-1 flex-row gap-[16px]">
              {article?.tags?.map((tag) => (
                <TagPill key={tag.id} tag={tag.name} />
              ))}
            </div>
            <div className="mt-[20px] flex flex-row items-center justify-center gap-[12px]">
              <SocialShare title={article?.title || ""} />
            </div>
          </div>
          <div className="mt-[32px] flex flex-col justify-between rounded-[24px] bg-[#FFF3ED] px-[20px] py-[22px] lg:flex-row">
            <div className="flex flex-1 flex-row gap-[16px]">
              {article?.author_avatar && (
                <img
                  src={`${process.env.REACT_APP_STRAPI_URL}${article.author_avatar.url}`}
                  alt={"author avatar"}
                  className="h-[56px] w-[56px] rounded-[50px]"
                />
              )}
              <div className="flex flex-1 flex-col gap-[6px]">
                <p className="font-inter text-[24px] font-medium leading-[100%] tracking-[-0.01em] text-[#131313]">
                  {article?.author?.charAt(0).toUpperCase() +
                    article?.author?.slice(1)}
                </p>
                <p className="text-[16px] font-normal leading-[140%] tracking-[0%] text-[#5A5A59]">
                  {article?.author_bio}
                </p>
              </div>
            </div>
            <div className="flex-1">
              <p className="mt-[24px] text-[14px] font-normal leading-[140%] tracking-[0%] text-[#5A5A59] lg:mt-0">
                Passionate about creating engaging content and connecting with
                audiences through meaningful storytelling.
              </p>
            </div>
          </div>
        </div>
        <div className="hidden flex-[0.1] flex-col items-center lg:flex">
          <SocialShare title={article?.title || ""} isVertical={true} />
        </div>
      </div>
      <div className="mx-[16px] mt-[56px] lg:mx-[75px] lg:mt-[128px]">
        <RelatedArticlesCarousel
          allArticles={articles?.pages.flatMap((page) => page.data) || []}
          currentArticle={article}
        />
      </div>
      <div className="mt-[80px] lg:mt-[128px]">
        <WebsiteFooter />
      </div>
    </SeoProvider>
  );
}

const TagPill = ({ tag }: { tag: string }) => {
  return (
    <div className="flex flex-row items-center justify-center rounded-[60px] border-[0.8px] border-[#D2D2D2] bg-[#FFFEFD] px-[20px] py-[14px]">
      <p className="font-inter text-[14px] font-medium leading-[100%] tracking-[0%] text-[#131313]">
        {tag}
      </p>
    </div>
  );
};
